const BPMN_NS = "http://www.omg.org/spec/BPMN/20100524/MODEL";

export type BpmnMeta =
  | {
      readonly kind: "ok";
      readonly processId: string;
      /** プロセスの name（無ければ空文字）。表示名の自動入力に使う。 */
      readonly processName: string;
    }
  /** XML として読めない。 */
  | { readonly kind: "invalid-xml" }
  /** 実行可能プロセスが無い（`isExecutable` が未指定か "true" で、id を持つものが 0 個）。 */
  | { readonly kind: "no-executable-process" }
  /** 実行可能プロセスが複数。どれを primary にするか決められない。 */
  | {
      readonly kind: "multiple-executable-processes";
      readonly processIds: readonly string[];
    };

/**
 * 持ち込みの BPMN から実行プロセスの id と名前を読む。
 *
 * モデルの新規作成（`primary_process_id` と表示名の自動入力）と、図の取り込み
 * （実行プロセスが変わるかの事前表示）で使う。送信してから backend に弾かれるより、
 * 選んだ時点で理由を出せるようにここで判定する。
 *
 * 件名（`case_title`）の宣言有無は見ない。宣言は `process_model.json` 側の情報で、
 * 作成時に use-create-model が必ず入れる。
 */
export function readBpmnMeta(xml: string): BpmnMeta {
  const doc = parseXml(xml);
  if (doc === null) return { kind: "invalid-xml" };

  const executables = Array.from(
    doc.getElementsByTagNameNS(BPMN_NS, "process"),
  ).filter(
    (element) =>
      // backend（SpiffWorkflow の ProcessParser）と同じ比較にする:
      // `node.get('isExecutable', 'true') == 'true'`。BPMN 仕様の既定は false だが、
      // backend は未指定を true として扱う
      (element.getAttribute("isExecutable") ?? "true") === "true" &&
      (element.getAttribute("id") ?? "") !== "",
  );

  const [only, ...rest] = executables;
  if (only === undefined) return { kind: "no-executable-process" };
  if (rest.length > 0) {
    return {
      kind: "multiple-executable-processes",
      processIds: executables.map(
        (element) => element.getAttribute("id") ?? "",
      ),
    };
  }

  return {
    kind: "ok",
    processId: only.getAttribute("id") ?? "",
    processName: only.getAttribute("name") ?? "",
  };
}

/**
 * DOMParser は壊れた XML でも例外を投げず、`parsererror` 要素を含む文書を返す
 * （ブラウザ・jsdom 共通）。そのため戻り値ではなく中身で判定する。
 */
function parseXml(xml: string): Document | null {
  if (xml.trim() === "") return null;

  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(xml, "application/xml");
  } catch {
    return null;
  }

  const hasError =
    doc.getElementsByTagName("parsererror").length > 0 ||
    doc.documentElement === null;

  return hasError ? null : doc;
}
